/**
 * Recent-search chips under the search box (STN client round 2026-08): the
 * three most recent saved searches, deduped on title. A click RESTORES the
 * full saved result set (zero LLM cost) — an upgrade over the mockup's
 * paste-text-only behavior, flagged to the client in the round's PR.
 * Pure display; the search page owns loading and the restore flow.
 */
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import type { MapsSavedSearch } from '../../models/maps.types';

const CHIP_COUNT = 3;
const CHIP_LABEL_MAX = 34;

interface ChipRow {
    searchId: string;
    label: string;
    fullTitle: string;
}

@Component({
    selector: 'maps-recent-search-chips',
    standalone: false,
    template: `
        <div class="recent" *ngIf="rows.length > 0" data-testid="recent-searches">
            <span class="lead">Recent:</span>
            <button
                type="button"
                class="chip"
                *ngFor="let row of rows"
                [attr.title]="row.fullTitle"
                [disabled]="disabled"
                (click)="restoreClicked.emit(row.searchId)">
                <svg viewBox="0 0 16 16" aria-hidden="true">
                    <circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" stroke-width="1.3" />
                    <path d="M8 4.8V8l2.2 1.6" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" />
                </svg>
                {{ row.label }}
            </button>
        </div>
    `,
    styleUrl: './recent-search-chips.component.scss',
})
export class RecentSearchChipsComponent implements OnChanges {
    @Input() searches: MapsSavedSearch[] = [];
    /** Chips disable while a search/restore is already in flight. */
    @Input() disabled = false;
    @Output() restoreClicked = new EventEmitter<string>();

    rows: ChipRow[] = [];

    ngOnChanges(): void {
        const seen = new Set<string>();
        const rows: ChipRow[] = [];
        for (const s of this.searches) {
            const key = s.title.trim().toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            rows.push({
                searchId: s.searchId,
                label: s.title.length > CHIP_LABEL_MAX ? `${s.title.slice(0, CHIP_LABEL_MAX).trimEnd()}…` : s.title,
                fullTitle: s.title,
            });
            if (rows.length === CHIP_COUNT) break;
        }
        this.rows = rows;
    }
}
